import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { htmlToExcerpt } from "../../lib/utils";
import PageHero from "../../components/public/PageHero";
import { MapPin, ArrowUpRight, Calendar } from "lucide-react";

const MONTHS = {
  es: ["Todos", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
  fr: ["Tous", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
  en: ["All", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  ar: ["الكل", "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"],
};

const TITLES = {
  es: { title: "Próximas actividades", subtitle: "Agenda de próximos encuentros, seminarios, ferias y jornadas académicas.", filterMonth: "Mes", filterYear: "Año", empty: "No hay actividades programadas para este período." },
  fr: { title: "Prochaines activités", subtitle: "Agenda des prochaines rencontres, séminaires, salons et journées académiques.", filterMonth: "Mois", filterYear: "Année", empty: "Aucune activité programmée pour cette période." },
  en: { title: "Upcoming activities", subtitle: "Schedule of upcoming meetings, seminars, trade shows and academic sessions.", filterMonth: "Month", filterYear: "Year", empty: "No activities scheduled for this period." },
  ar: { title: "الأنشطة القادمة", subtitle: "جدول اللقاءات والندوات والمعارض والأيام الأكاديمية القادمة.", filterMonth: "الشهر", filterYear: "السنة", empty: "لا توجد أنشطة مبرمجة لهذه الفترة." },
};

/**
 * Public "Upcoming activities" page.
 *
 * Fetches `?upcoming_only=1` so items automatically drop off the list once
 * their `starts_at` is in the past — no admin action required. Provides two
 * client-side filters (month + year) that operate on the fetched set.
 */
export default function ProximasActividades() {
  const { lang, t, pick } = useLang();
  const L = TITLES[lang] || TITLES.es;
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(0);   // 0 = all
  const [year, setYear] = useState("all"); // "all" = all years

  useEffect(() => {
    api.get("/events?family=actividades&upcoming_only=1")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  // Distinct years present in the upcoming set, sorted asc.
  const years = useMemo(() => {
    const set = new Set();
    for (const it of items) if (it.starts_at) set.add(new Date(it.starts_at).getFullYear());
    return Array.from(set).sort((a, b) => a - b);
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (!it.starts_at) return false;
      const d = new Date(it.starts_at);
      if (year !== "all" && d.getFullYear() !== Number(year)) return false;
      if (month !== 0 && d.getMonth() + 1 !== month) return false;
      return true;
    });
  }, [items, month, year]);

  return (
    <div data-testid="events-page-proximas">
      <PageHero title={L.title} subtitle={L.subtitle} />

      <section className="border-b border-gray-100 bg-white sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex gap-4 flex-wrap items-center">
          <span className="text-xs uppercase tracking-widest text-gray-500 inline-flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" /> {L.filterMonth}
          </span>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            data-testid="proximas-month"
            className="text-xs px-3 py-1.5 border border-gray-200 bg-white focus:border-[#0F2040] outline-none"
          >
            {MONTHS[lang].map((m, i) => (
              <option key={i} value={i}>{m}</option>
            ))}
          </select>

          <span className="text-xs uppercase tracking-widest text-gray-500 ml-4">{L.filterYear}</span>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            data-testid="proximas-year"
            className="text-xs px-3 py-1.5 border border-gray-200 bg-white focus:border-[#0F2040] outline-none"
          >
            <option value="all">{MONTHS[lang][0]}</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>

          <span className="text-xs text-gray-400 ml-auto" data-testid="proximas-count">
            {filtered.length} {filtered.length === 1
              ? (lang === "es" ? "actividad" : lang === "fr" ? "activité" : lang === "en" ? "activity" : "نشاط")
              : (lang === "es" ? "actividades" : lang === "fr" ? "activités" : lang === "en" ? "activities" : "أنشطة")}
          </span>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        {filtered.length === 0 ? (
          <p className="text-gray-500 text-center py-20" data-testid="proximas-empty">{L.empty}</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((e) => (
              <article
                key={e.id}
                className="bg-white border border-gray-100 hover:shadow-xl transition-shadow group flex flex-col"
                data-testid={`proxima-card-${e.slug}`}
              >
                {e.cover_image && (
                  <div className="aspect-video overflow-hidden bg-gray-100">
                    <img src={e.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                )}
                <div className="p-7 flex-1 flex flex-col">
                  <p className="text-xs uppercase tracking-widest text-[#C8102E] mb-3">
                    {new Date(e.starts_at).toLocaleDateString(
                      lang === "es" ? "es-ES" : lang === "fr" ? "fr-FR" : lang === "ar" ? "ar-MA" : "en-US",
                      { day: "numeric", month: "long", year: "numeric" }
                    )}
                  </p>
                  <h3 className="font-serif text-2xl text-[#0F2040] mb-3 leading-tight">{pick(e, "title")}</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-3 flex-1">
                    {htmlToExcerpt(pick(e, "description"), 260)}
                  </p>
                  {e.location && (
                    <p className="text-xs text-gray-500 flex items-center gap-1.5 mb-3">
                      <MapPin className="w-3 h-3" /> {e.location}
                    </p>
                  )}
                  <Link
                    to={`/actividades/${e.slug}`}
                    className="text-xs uppercase tracking-widest text-[#0F2040] inline-flex items-center gap-1.5 hover:text-[#C8102E] transition-colors mt-1"
                  >
                    {t("common.readMore")} <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
